import { getDateTimeParts } from '@sveltia/utils/datetime';
import { stripSlashes } from '@sveltia/utils/string';
import { sanitize } from 'isomorphic-dompurify';
import { parseInline } from 'marked';
import { parseEntities } from 'parse-entities';

import { TEMPLATE_TAG_REPLACE_REGEX } from '$lib/services/common/template/constants';
import { processNestedTemplates } from '$lib/services/common/template/nested';
import { applyTransformations, parseTransformations } from '$lib/services/common/transformations';
import {
  getIndexFile,
  isCollectionIndexFile,
} from '$lib/services/contents/collection/entries/index-file';
import { getField, getFieldDisplayValue } from '$lib/services/contents/entry/fields';

/**
 * @import {
 * CommitAuthor,
 * Entry,
 * FlattenedEntryContent,
 * InternalCollection,
 * InternalLocaleCode,
 * } from '$lib/types/private';
 * @import { RawEntryContent } from '$lib/types/public';
 */

/**
 * @typedef {object} ReplacerSubContext
 * @property {string} slug Entry slug.
 * @property {string} entryPath Entry path.
 * @property {string | undefined} basePath Base path for the entry.
 * @property {string[]} locales Enabled locales for the entry.
 * @property {Date | undefined} commitDate Commit date.
 * @property {CommitAuthor | undefined} commitAuthor Commit author.
 */

/**
 * @typedef {object} ReplaceContext
 * @property {FlattenedEntryContent} content Entry content.
 * @property {string} collectionName Collection name.
 * @property {ReplacerSubContext} replaceSubContext Context for the `replaceSub` function.
 * @property {InternalLocaleCode} defaultLocale Default locale.
 */

const BODY_HEADER_REGEX = /^#+\s+(?<header>.+?)(?:\s+\{#.+?\})?\s*$/m;

/**
 * Parse the given entry summary as Markdown and sanitize HTML with a few exceptions if the Markdown
 * option is enabled. Also, parse HTML character references (entities).
 * @param {string} str Original string.
 * @param {object} [options] Options.
 * @param {boolean} [options.allowMarkdown] Whether to allow Markdown and return HTML string.
 * @returns {string} Parsed string.
 */
export const sanitizeEntrySummary = (str, { allowMarkdown = false } = {}) => {
  str = /** @type {string} */ (parseInline(str));

  if (allowMarkdown) {
    str = parseEntities(str);
  }

  str = sanitize(str, { ALLOWED_TAGS: allowMarkdown ? ['strong', 'em', 'code'] : [] });

  if (!allowMarkdown) {
    str = parseEntities(str);
  }

  return str.trim();
};

/**
 * Determine an entry summary from the given content. Fields other than `title` should be defined
 * with the `identifier_field` collection option as per the Netlify/Decap CMS document. We also look
 * for the `name` and `label` properties as well as a header in the Markdown `body` as a fallback.
 * @param {FlattenedEntryContent | RawEntryContent} content Content.
 * @param {object} options Options.
 * @param {string} [options.identifierField] Field name to identify the title.
 * @param {boolean} [options.useBody] Whether to fall back to a header in the Markdown `body`.
 * @returns {string} Entry summary. Can be an empty string if it cannot be determined.
 * @see https://decapcms.org/docs/configuration-options/#identifier_field
 */
export const getEntrySummaryFromContent = (
  content,
  { identifierField = 'title', useBody = true } = {},
) => {
  const idField = [identifierField, 'title', 'name', 'label'].find(
    (fieldName) => typeof content[fieldName] === 'string' && !!content[fieldName].trim(),
  );

  if (idField) {
    return content[idField].trim();
  }

  // Find a header in Markdown, excluding an anchor suffix
  // https://vitepress.dev/guide/markdown#custom-anchors
  if (useBody && typeof content.body === 'string') {
    return content.body.match(BODY_HEADER_REGEX)?.groups?.header ?? '';
  }

  return '';
};

/**
 * Replacer subroutine.
 * @param {string} tag Field name or one of special tags.
 * @param {ReplacerSubContext} context Context.
 * @returns {string | Date | undefined} Replaced value or `undefined` if the tag is not recognized.
 */
export const replaceSub = (tag, context) => {
  const { slug, entryPath, basePath, locales, commitDate, commitAuthor } = context;

  if (tag === 'slug') {
    return slug;
  }

  if (tag === 'locales') {
    return locales.sort((a, b) => a.localeCompare(b)).join(', ');
  }

  if (tag === 'dirname') {
    let dirPath = entryPath.replace(/[^/]+$/, '');

    if (basePath) {
      // Remove basePath prefix with boundary awareness
      const prefix = basePath.endsWith('/') ? basePath : `${basePath}/`;

      if (dirPath.startsWith(prefix)) {
        dirPath = dirPath.slice(prefix.length);
      } else if (dirPath.startsWith(basePath)) {
        dirPath = dirPath.slice(basePath.length);
      }
    }

    return stripSlashes(dirPath);
  }

  if (tag === 'filename') {
    return /** @type {string} */ (entryPath.split('/').pop()).split('.').shift();
  }

  if (tag === 'extension') {
    return /** @type {string} */ (entryPath.split('/').pop()).split('.').pop();
  }

  if (tag === 'commit_date') {
    return commitDate ?? '';
  }

  if (tag === 'commit_author') {
    return commitAuthor?.name || commitAuthor?.login || commitAuthor?.email;
  }

  return undefined;
};

/**
 * Replacer.
 * @param {string} placeholder Field name or one of special tags. May contain transformations.
 * @param {ReplaceContext} context Context.
 * @returns {string} Replaced string.
 */
export const replace = (placeholder, context) => {
  const { content: valueMap, collectionName, replaceSubContext, defaultLocale } = context;
  const { value: tag, transformations: parsedTransformations } = parseTransformations(placeholder);
  const keyPath = tag.replace(/^fields\./, '');
  const getFieldArgs = { collectionName, valueMap, keyPath };
  let value = replaceSub(tag, replaceSubContext);

  // Process nested templates in transformation arguments
  const transformations = processNestedTemplates(parsedTransformations, (innerTag) =>
    replace(innerTag, context),
  );

  if (value === undefined) {
    // If the `date` transformation is defined, e.g. `{{publish_date | date('YYYY-MM')}}`, use the
    // raw field value from the entry content. Otherwise, use the field display value. This is to
    // avoid applying the transformation to the display value, which leads to unexpected results.
    // Also use raw value for ternary transformations to preserve boolean truthiness.
    value = transformations.some(({ method }) => method === 'date' || method === 'ternary')
      ? valueMap[keyPath]
      : getFieldDisplayValue({ ...getFieldArgs, locale: defaultLocale });
  }

  if (value === undefined) {
    return '';
  }

  if (value instanceof Date && !transformations.length) {
    const { year, month, day } = getDateTimeParts({ date: value });

    return `${year}-${month}-${day}`;
  }

  if (transformations.length) {
    value = applyTransformations({
      fieldConfig: getField({ ...getFieldArgs }),
      value,
      transformations,
      locale: defaultLocale,
    });
  }

  return String(value);
};

/**
 * Get the given entry’s summary that can be displayed in the entry list and other places. Format it
 * with the summary template if necessary, or simply use the `title` or similar field in the entry.
 * @param {InternalCollection} collection Entry’s collection.
 * @param {Entry} entry Entry.
 * @param {object} [options] Options.
 * @param {InternalLocaleCode} [options.locale] Target locale. The default locale is used if
 * omitted.
 * @param {boolean} [options.useTemplate] Whether to use the collection’s `summary` template if
 * available.
 * @param {boolean} [options.allowMarkdown] Whether to allow Markdown and return HTML string.
 * @returns {string} Formatted entry summary.
 * @see https://decapcms.org/docs/configuration-options/#summary
 * @see https://sveltiacms.app/en/docs/collections/entries#summaries
 */
export const getEntrySummary = (
  collection,
  entry,
  { locale, useTemplate = false, allowMarkdown = false } = {},
) => {
  if (isCollectionIndexFile(collection, entry)) {
    return /** @type {string} */ (getIndexFile(collection)?.label);
  }

  const {
    _type,
    name: collectionName,
    _i18n: { defaultLocale },
  } = collection;

  const {
    _file: { basePath } = {},
    identifier_field: identifierField = 'title',
    summary: summaryTemplate,
  } = _type === 'entry' ? collection : {};

  const { locales, slug, commitDate, commitAuthor } = entry;

  const { content = {}, path: entryPath = '' } =
    locales[locale ?? defaultLocale] ?? Object.values(locales)[0] ?? {};

  if (!useTemplate || !summaryTemplate) {
    return sanitizeEntrySummary(
      getEntrySummaryFromContent(content, { identifierField }) || slug.replaceAll('-', ' '),
      { allowMarkdown },
    );
  }

  /** @type {ReplaceContext} */
  const replaceContext = {
    content,
    collectionName,
    replaceSubContext: {
      slug,
      entryPath,
      basePath,
      locales: Object.keys(locales),
      commitDate,
      commitAuthor,
    },
    defaultLocale,
  };

  return sanitizeEntrySummary(
    summaryTemplate.replace(TEMPLATE_TAG_REPLACE_REGEX, (_match, placeholder) =>
      replace(placeholder, replaceContext),
    ),
    { allowMarkdown },
  );
};
